import React, { useState } from 'react';
import { SiJavascript, SiTypescript, SiPython, SiReact, SiNextdotjs,
    SiTailwindcss, SiHtml5, SiStyledcomponents, SiNodedotjs, SiExpress, SiDocker,
    SiPostgresql, SiGit, SiFigma, SiLinux, SiAmazon, SiCplusplus } from "react-icons/si";
import { FaJava, FaRust } from "react-icons/fa";

const skillIcons = {
    JavaScript: SiJavascript,
    TypeScript: SiTypescript,
    Python: SiPython,
    "C/C++": SiCplusplus,
    Java: FaJava,
    Rust: FaRust,
    React: SiReact,
    "Next.js": SiNextdotjs,
    TailwindCSS: SiTailwindcss,
    "HTML/CSS": SiHtml5,
    "Styled Components": SiStyledcomponents,
    "Node.js": SiNodedotjs,
    "Express.js": SiExpress,
    Docker: SiDocker,
    PostgreSQL: SiPostgresql,
    Git: SiGit,
    Figma: SiFigma,
    Linux: SiLinux,
    AWS: SiAmazon
};

const categoryLabels = {
    languages: 'Languages',
    frontend: 'Frontend',
    backend: 'Backend',
    tools: 'Tools'
};

const SkillsDisplay = ({ skills }) => {
    const [hoveredSkill, setHoveredSkill] = useState(null);

    return (
        <section id="skills" className="py-20 md:py-32 relative">
            <div className="max-w-6xl mx-auto px-4 md:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-6xl font-display mb-4 text-white tracking-wide">
                        My Skills
                    </h2>
                    <p className="text-white/40 max-w-2xl mx-auto text-sm font-mono">
                        Technologies and tools I work with
                    </p>
                </div>

                {/* Skills Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(skills).map(([category, items], categoryIndex) => (
                        <div
                            key={category}
                            className="p-5 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all duration-300"
                            style={{ animationDelay: `${categoryIndex * 0.1}s` }}
                        >
                            {/* Category Header */}
                            <div className="flex items-center gap-2 mb-4">
                                <div className="p-2 rounded-lg bg-white/5 text-white/60">
                                    <CategoryIcon category={category} />
                                </div>
                                <h3 className="text-base font-semibold text-white">{categoryLabels[category]}</h3>
                            </div>

                            {/* Skills List */}
                            <div className="space-y-1.5">
                                {items.map((skill, index) => {
                                    const IconComponent = skillIcons[skill.name];
                                    const isHovered = hoveredSkill === `${category}-${index}`;

                                    return (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/5 transition-all duration-200 cursor-default"
                                            onMouseEnter={() => setHoveredSkill(`${category}-${index}`)}
                                            onMouseLeave={() => setHoveredSkill(null)}
                                        >
                                            {IconComponent && (
                                                <IconComponent className={`text-base transition-all duration-200 ${isHovered ? 'text-white' : 'text-white/40'}`} />
                                            )}
                                            <span className="text-white/60 text-sm flex-1">{skill.name}</span>
                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                                skill.level === 'Advanced'
                                                    ? 'bg-white/10 text-white/70'
                                                    : 'bg-white/5 text-white/30'
                                            }`}>
                                                {skill.level === 'Advanced' ? 'ADV' : 'INT'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const CategoryIcon = ({ category, className = "w-4 h-4" }) => {
    const icons = {
        languages: (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
        ),
        frontend: (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        backend: (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
        ),
        tools: (
            <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        )
    };
    return icons[category] || icons.tools;
};

export default SkillsDisplay;
